import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import toast from "react-hot-toast"
import { BlogCard, BlogTable, Filter } from "./components"
import { IBlogItem } from "interfaces"
import { isHaveActionPermission } from "utills/helpers"

export const BlogList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IBlogItem>({
    resource: "mkt/blogs",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_hot_position: logicalFilters.find((item) => item.field === "is_hot_position")?.value || "",
      category: logicalFilters.find((item) => item.field === "category")?.value || "",
      status: logicalFilters.find((item) => item.field === "status")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("blog", "mkt", "edit")) return;
    if (window.confirm(`Are you sure you want to ${!isActive ? "active": "inactive"} this blog?`)) {
      updateMutation({
        resource: "mkt/blogs",
        id,
        meta: {
          URLSuffix: !isActive ? "active" : "inactive",
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
      {...(isHaveActionPermission("blog", "mkt", "create") && {
        additionalLabel: (
          <Link to={"/mkt/blog/create"}>
            <Button>
              <span className="sr-only">Create New Blog</span>
              <PlusIcon className="w-4 h-4 text-white" />{" "}
              <span className="hidden sm:block sm:ml-2">Create New Blog</span>
            </Button>
          </Link>
        ),
      })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <BlogCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <BlogTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_items ?? 0,
                total: tableQuery?.data?.paging.total_blogs
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}